package guide.kuki.app.engine

import kotlin.math.abs
import kotlin.math.ln
import kotlin.math.round
import kotlin.math.sqrt

private const val ALPHABET = "abcdefghijklmnopqrstuvwxyz"

/**
 * Squareness tolerance (in natural log units of the sub-block aspect
 * ratio) for [Block.calcDivs]. Grids whose aspect-ratio score is within
 * this much of the squarest available are considered equivalent, and
 * the tie is broken by picking the one with the most cells (so we get
 * the best L1+ resolution we can without sacrificing squareness).
 *
 * `0.0` ⇒ pick whichever grid has the squarest sub-blocks; if multiple
 * grids tie at FP-exact `|ln r|`, pick the one with the most cells.
 *
 * Raising this loosens the squareness constraint in favour of cells.
 * `0.2` ≈ 22 % aspect-ratio leeway; `0.5` ≈ 65 %; etc.
 */
private const val SQUARENESS_TOLERANCE_LN = 0.2

/**
 * Floor on the number of sub-block cells [Block.calcDivs] will produce
 * for non-degenerate blocks. Sub-blocks exist to give finer addresses,
 * so even awkwardly-shaped blocks should use most of the 26-letter
 * alphabet rather than collapsing to `[2,1]` or `[3,1]` just because
 * the squarest grid within tolerance happens to be small. When the
 * pass-2 pick has fewer cells than this, pass 3 ignores the tolerance
 * gate and chooses the squarest grid with at least [MIN_CELLS] cells.
 */
private const val MIN_CELLS = 20

/**
 * A quadrilateral (or triangle for sector tips) defined by 4 corner [Coords].
 *
 * Point ordering:
 *   pts[0] = inner-left,  pts[1] = outer-left
 *   pts[2] = inner-right, pts[3] = outer-right
 *
 * For sector tips where inner edges converge, pts[1] == pts[3].
 */
class Block(
    var idx: Int = -1,
    val pts: Array<Coords> = emptyArray(),
) {
    val isValid: Boolean = pts.isNotEmpty() && pts[0].lat != 0.0

    var slice: Slice? = null
    private var _latLng: LatLng? = null
    var address: String = ""
    var subLetters: String = ""
    var divs: IntArray? = null
    private var _subBlock: Block? = null

    fun isSector(): Boolean = pts.size >= 4 && pts[1] == pts[3]

    fun updateLatLng(latlng: LatLng) {
        val current = _latLng
        if (current == null || current.lat != latlng.lat || current.lng != latlng.lng) {
            _subBlock = null
        }
        _latLng = latlng
    }

    fun resolveLatLng(): LatLng {
        val current = _latLng
        if (current != null) return current

        var lat = 0.0
        var lng = 0.0
        for (pt in pts) {
            lat += pt.lat
            lng += pt.lon
        }
        lat /= pts.size
        lng /= pts.size

        val result = LatLng(lat, lng)
        _latLng = result
        return result
    }

    fun getShapeString(): String {
        val dc = distance(pts[0].latlon, pts[2].latlon)
        val dr = distance(pts[0].latlon, pts[1].latlon)
        // Round (rather than truncate) so 24.9997 m reports as 25, not 24.
        val dcStr = if (dc >= 10) round(dc).toInt().toString() else (round(dc * 10) / 10.0).toString()
        val drStr = if (dr >= 10) round(dr).toInt().toString() else (round(dr * 10) / 10.0).toString()
        return "$dcStr x $drStr m"
    }

    fun containsLatLon(latLon: LatLng): Boolean {
        val pt = doubleArrayOf(latLon.lng, latLon.lat)
        return if (isSector()) {
            toLeftOfLine(pt, pts[0].lonlat, pts[1].lonlat)
                    && toLeftOfLine(pt, pts[1].lonlat, pts[2].lonlat)
                    && toLeftOfLine(pt, pts[2].lonlat, pts[0].lonlat)
        } else {
            toLeftOfLine(pt, pts[0].lonlat, pts[1].lonlat)
                    && toLeftOfLine(pt, pts[1].lonlat, pts[3].lonlat)
                    && toLeftOfLine(pt, pts[3].lonlat, pts[2].lonlat)
                    && toLeftOfLine(pt, pts[2].lonlat, pts[0].lonlat)
        }
    }

    /**
     * Picks an `[nr, nc]` sub-grid where `MIN_CELLS ≤ nr × nc ≤ 26`
     * (the 26-letter alphabet cap, with a floor so sub-blocks actually
     * give useful address density). Among grids in that cell-count
     * window, the one with the squarest sub-blocks wins — min `|ln r|`
     * where `r = (dr/nr) / (dc/nc)` is the sub-block aspect ratio.
     *
     * Degenerate guard: if either edge has zero length (sector tip
     * whose inner edge collapsed), return `[1, 1]` — the block has no
     * meaningful interior to subdivide, so the cell floor is skipped.
     */
    fun calcDivs(): IntArray {
        val dc = distance(pts[0].latlon, pts[2].latlon)
        val dr = distance(pts[0].latlon, pts[1].latlon)
        if (dr <= 0.0 || dc <= 0.0) return intArrayOf(1, 1)

        var bestScore = Double.MAX_VALUE
        var bestNr = 1
        var bestNc = 1
        for (nr in 1..26) {
            for (nc in 1..26) {
                val cells = nr * nc
                if (cells < MIN_CELLS || cells > 26) continue
                val s = abs(ln((dr / nr) / (dc / nc)))
                if (s < bestScore) {
                    bestScore = s
                    bestNr = nr
                    bestNc = nc
                }
            }
        }
        return intArrayOf(bestNr, bestNc)
    }

    /**
     * Earlier two-pass sub-grid picker kept around for comparison —
     * preferred the squarest grid (within [SQUARENESS_TOLERANCE_LN])
     * with the most cells, with no hard floor. Replaced by the simpler
     * MIN_CELLS-floored squarest-only [calcDivs] above; left in source
     * so the cells-vs-squareness trade-off is still visible if we want
     * to revisit. Not called from anywhere.
     */
    @Suppress("unused")
    fun calcDivsB(): IntArray {
        val dc = distance(pts[0].latlon, pts[2].latlon)
        val dr = distance(pts[0].latlon, pts[1].latlon)
        if (dr <= 0.0 || dc <= 0.0) return intArrayOf(1, 1)

        var minScore = Double.MAX_VALUE
        for (nr in 1..26) {
            for (nc in 1..26) {
                if (nr * nc > 26) continue
                val s = abs(ln((dr / nr) / (dc / nc)))
                if (s < minScore) minScore = s
            }
        }
        var bestNr = 1
        var bestNc = 1
        var bestCells = 0
        for (nr in 1..26) {
            for (nc in 1..26) {
                if (nr * nc > 26) continue
                val s = abs(ln((dr / nr) / (dc / nc)))
                if (s > minScore + SQUARENESS_TOLERANCE_LN) continue
                val cells = nr * nc
                if (cells > bestCells) {
                    bestCells = cells
                    bestNr = nr
                    bestNc = nc
                }
            }
        }
        return intArrayOf(bestNr, bestNc)
    }

    fun getPolygon(): List<DoubleArray> {
        return if (isSector()) {
            listOf(pts[0].latlon, pts[1].latlon, pts[2].latlon)
        } else {
            listOf(pts[0].latlon, pts[1].latlon, pts[3].latlon, pts[2].latlon)
        }
    }

    fun getSlicePolygon(): List<List<DoubleArray>> {
        val polygons = mutableListOf<List<DoubleArray>>()
        val s = slice ?: return polygons

        if (s.isSector()) {
            polygons.add(listOf(pts[1].latlon, s.r0[s.lastIdx].latlon, pts[3].latlon))
        } else {
            polygons.add(listOf(pts[1].latlon, s.r0[s.lastIdx].latlon, s.r1[s.lastIdx].latlon, pts[3].latlon))
        }
        polygons.add(listOf(s.r0[0].latlon, pts[0].latlon, pts[2].latlon, s.r1[0].latlon))
        return polygons
    }

    fun calcSubBlock(rIdx: Int, cIdx: Int, rDivs: Int, cDivs: Int): Block {
        val dXi = 1.0 / rDivs
        val xiA = rIdx * dXi
        val xiB = xiA + dXi

        val radialBlock = arrayOf(
            interpolateXi(xiA, pts[0].latlon, pts[1].latlon),
            interpolateXi(xiB, pts[0].latlon, pts[1].latlon),
            interpolateXi(xiA, pts[2].latlon, pts[3].latlon),
            interpolateXi(xiB, pts[2].latlon, pts[3].latlon)
        )

        val dXiC = 1.0 / cDivs
        val xiC0 = cIdx * dXiC
        val xiC1 = xiC0 + dXiC

        val subBlockPts = arrayOf(
            Coords(interpolateXi(xiC0, radialBlock[0], radialBlock[2])),
            Coords(interpolateXi(xiC0, radialBlock[1], radialBlock[3])),
            Coords(interpolateXi(xiC1, radialBlock[0], radialBlock[2])),
            Coords(interpolateXi(xiC1, radialBlock[1], radialBlock[3]))
        )
        val subIdx = cIdx * rDivs + rIdx
        return Block(subIdx, subBlockPts)
    }

    /**
     * Resolves a sub-block by alphabet letter. Returns null when the
     * letter is unknown (not in `a..z`) or refers to a grid cell beyond
     * this block's `nr × nc` (e.g. asking for `'z'` on a 5×5 = 25-cell
     * grid). The previous behaviour silently produced an extrapolated
     * polygon outside the parent's geometry.
     */
    fun getSubBlockByLetter(letter: Char): Block? {
        if (divs == null) divs = calcDivs()
        val d = divs!!
        val letterIdx = ALPHABET.indexOf(letter)
        if (letterIdx < 0 || letterIdx >= d[0] * d[1]) return null
        val cIdx = letterIdx / d[0]
        val rIdx = letterIdx - cIdx * d[0]
        val sub = calcSubBlock(rIdx, cIdx, d[0], d[1])
        sub.resolveLatLng()
        sub.subLetters = subLetters + letter
        return sub
    }

    fun resolveSubBlock(): Block {
        val cached = _subBlock
        if (cached != null) return cached

        val latlng = resolveLatLng()
        if (divs == null) divs = calcDivs()
        val d = divs!!
        val nr = d[0]
        val nc = d[1]

        val pt = doubleArrayOf(latlng.lng, latlng.lat)
        var idxR = nr - 1
        var idxC = nc - 1

        for (i in 0 until nr) {
            val block = calcSubBlock(i, 0, nr, 1)
            if (toLeftOfLine(pt, block.pts[1].lonlat, block.pts[3].lonlat)) {
                idxR = i
                break
            }
        }
        for (i in 0 until nc) {
            val block = calcSubBlock(0, i, 1, nc)
            if (toLeftOfLine(pt, block.pts[3].lonlat, block.pts[2].lonlat)) {
                idxC = i
                break
            }
        }

        val result = calcSubBlock(idxR, idxC, nr, nc)
        result.updateLatLng(latlng)
        val sIdx = result.idx
        result.subLetters = subLetters + ALPHABET[sIdx]
        _subBlock = result
        return result
    }

    fun clearSubBlock() {
        _subBlock = null
    }
}
